import React, { Fragment, useEffect } from 'react'
import { Route, Switch } from 'react-router-dom'
import App from 'modules/app/App'
import * as actions from 'store/actions'
import {
  fetchTeams_action,
  getActiveSubscription_action,
  getTotalItemsCount_action,
  getAdminPageTemplates_action,
  getTeamPageTemplates_action,
  getCompany_action,
  getMembers_action,
  setCompanyStorageUtilized_action,
  getCategories_action,
  getDrafts_action,
  getPrivateAndFavoriteContents_action
} from 'store/actions'
import { Flip, ToastContainer } from 'react-toastify'
import { getCompanyStorageSize, usePrevious, useQuery } from 'utils/helpers'
import { store } from 'store/store'
import { connect } from 'react-redux'
import { lazyLoader } from 'utils/storageUtils'
import { getAppVersion_api } from 'store/apis'
import SocialLoginPassword from 'modules/auth/SocialLoginPassword'

const Register = React.lazy(() => import('modules/auth/Register'))
const Login = React.lazy(() => import('modules/auth/Login'))
const OnBoardingComponent = React.lazy(() => import('modules/auth/OnBoarding'))
const SharedPage = React.lazy(() => import('modules/pages/components/SharedPage'))
const SocialLoginWithPassword = React.lazy(() => import('modules/auth/SocialLoginPassword'))

const checkAppVersion = async () => {
  const res = await getAppVersion_api()
  store.dispatch(actions.setChangeLogs_action(res))
}

function DefaultLayout(props) {
  const {
    isLoggedIn,
    user,
    restoreSession,
    company,
    getActiveSubscription,
    getCompany,
    getCategories,
    getDrafts,
    getPrivateAndFavoriteContents,
    getAdminPageTemplates,
    getTeamPageTemplates,
    getMembers,
    fetchTeams,
    getTotalItemsCount
  } = props
  const query = useQuery()
  const token = query.get('t')
  const prevUserId = usePrevious(user ? user.id : null)

  // Get all pages and responses and templaets
  useEffect(() => {
    if (isLoggedIn) getActiveSubscription()
  }, [getActiveSubscription, isLoggedIn])
  useEffect(() => {
    if (isLoggedIn) getCategories()
  }, [getCategories, isLoggedIn])
  useEffect(() => {
    if (isLoggedIn) getDrafts()
  }, [getDrafts, isLoggedIn])
  useEffect(() => {
    if (isLoggedIn) {
      const get = async () => {
        getPrivateAndFavoriteContents({ type: 'response' })
        getPrivateAndFavoriteContents({ type: 'page' })
      }
      get()
    }
  }, [getPrivateAndFavoriteContents, isLoggedIn])
  useEffect(() => {
    if (isLoggedIn) getAdminPageTemplates()
  }, [getAdminPageTemplates, isLoggedIn])
  useEffect(() => {
    if (isLoggedIn) getTeamPageTemplates()
  }, [getTeamPageTemplates, isLoggedIn])
  useEffect(() => {
    if (isLoggedIn) fetchTeams()
  }, [fetchTeams, isLoggedIn])
  useEffect(() => {
    if (isLoggedIn) checkAppVersion()
  }, [fetchTeams, isLoggedIn])
  useEffect(() => {
    if (!company.loading) getMembers()
  }, [getMembers, company.loading])
  useEffect(() => {
    if (isLoggedIn) getTotalItemsCount()
  }, [getTotalItemsCount, isLoggedIn])

  useEffect(() => {
    if (!company.loading && company.company.id) {
      getCompanyStorageSize(company.company.id).then((totalSize) => {
        store.dispatch(setCompanyStorageUtilized_action(totalSize))
      })
    }
  }, [company.loading, company.company.id])

  useEffect(() => {
    if (!isLoggedIn) {
      if (token) {
        localStorage.setItem('token', token)
      }
      setTimeout(() => restoreSession(), 500)
    } else {
      if (user && user.membership && prevUserId !== user.id) {
        getCompany()
        if (window.amplitude) {
          window.amplitude.getInstance().setUserId(user.email)
          const identify = new window.amplitude.Identify().setOnce('user_member_code', user.member_code)
          window.amplitude.identify(identify)
          if (user.membership && user.membership.company) {
            window.amplitude.getInstance().setGroup('company', user.membership.company.name)
          }
        }
      }
    }
  }, [user, isLoggedIn, restoreSession, getCompany, token, prevUserId])

  return (
    <Fragment>
      <Switch>
        <Route
          exact
          path='/login'
          name='Login'
          render={(props) => (
            <React.Suspense fallback={lazyLoader()}>
              <Login {...props} />
            </React.Suspense>
          )}
        />
        <Route
          exact
          name='SocialLoginPassword'
          path='/social-login'
          render={(props) => (
            <React.Suspense fallback={lazyLoader()}>
              <SocialLoginWithPassword {...props} />
            </React.Suspense>
          )}
        />
        <Route
          exact
          path='/register'
          name='Register'
          render={(props) => (
            <React.Suspense fallback={lazyLoader()}>
              <Register {...props} />
            </React.Suspense>
          )}
        />
        <Route
          exact
          path='/onboarding'
          name='OnBoarding'
          render={(props) => (
            <React.Suspense fallback={lazyLoader()}>
              <OnBoardingComponent {...props} />
            </React.Suspense>
          )}
        />
        <Route
          name='SharedPage'
          path='/guest/page/:uuid'
          render={(props) => (
            <React.Suspense fallback={lazyLoader()}>
              <SharedPage {...props} />
            </React.Suspense>
          )}
        />
        <Route path='/' name='Home' render={(props) => isLoggedIn && <App {...props} />} />
      </Switch>
      <ToastContainer className='toastify_container' transition={Flip} autoClose={3000} hideProgressBar={true} position='bottom-right' closeOnClick={false} newestOnTop={true} draggable={false} />
    </Fragment>
  )
}
const mapStateToProps = (state) => ({
  user: state.auth.user,
  isLoggedIn: state.auth.isLoggedIn,
  company: state.company
})
const mapActionsToProps = (dispatch) => ({
  restoreSession: () => dispatch(actions.restoreSession()),
  getCompany: () => dispatch(getCompany_action()),
  getActiveSubscription: () => dispatch(getActiveSubscription_action()),
  getCategories: () => dispatch(getCategories_action()),
  getDrafts: () => dispatch(getDrafts_action()),
  getPrivateAndFavoriteContents: (filters) =>
    new Promise((res, rej) => {
      dispatch(getPrivateAndFavoriteContents_action(filters, res, rej))
    }),
  getAdminPageTemplates: () => dispatch(getAdminPageTemplates_action()),
  getTeamPageTemplates: () => dispatch(getTeamPageTemplates_action()),
  getMembers: () => dispatch(getMembers_action()),
  fetchTeams: () => dispatch(fetchTeams_action()),
  getTotalItemsCount: () => dispatch(getTotalItemsCount_action())
})
const ConnectDefaultLayout = connect(mapStateToProps, mapActionsToProps)(DefaultLayout)
export default ConnectDefaultLayout
